import { useRef } from 'react';
import { UniqueIdentifier } from '@dnd-kit/core';
import classNames from 'classnames';
import { AnimatePresence, domAnimation, LazyMotion, motion } from 'framer-motion';
import { useParams } from 'react-router';

import styles from '../../../../scss/live-event/base/modules/accordion modules/item.module.scss';
import { AccordionItem as IAccordionItem } from '../../../../types/working-model';
import Icon, { ICONS } from '../../../general-ui/icon';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import useTranslationRoute from '../../hooks/use-translation-route';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import { getStylingOverrides } from '../../utils';

interface IAccordionItemProps {
	moduleId: number;
	item: IAccordionItem;
	handleOpenItem(itemId: UniqueIdentifier): void;
	open: boolean;
	tabIndex: number;
}

const AccordionItem = (props: IAccordionItemProps) => {
	const { moduleId, item, handleOpenItem, open, tabIndex } = props;

	const { language } = useParams<ParamsProps>();

	const descriptionRef = useRef<HTMLInputElement | null>(null);

	const translationRoute = useTranslationRoute();
	const { t } = useTranslate([translationRoute, "homepage"]);

	const titleFallback = item?.title?.[language] ?? item?.title?.base ?? '';
	const descriptionFallback = item?.description?.[language] ?? item?.description?.base ?? '';

	const title = t(`${translationRoute}:accordion.${moduleId}.${item.id}.title`, titleFallback as string);
	const description = t(`${translationRoute}:accordion.${moduleId}.${item.id}.description`, descriptionFallback as string);

	const {
		titleOverrides,
	} = getStylingOverrides(item.styling_overrides);

	const itemOpen = open;
	const itemHeight = descriptionRef.current?.offsetHeight ? `${descriptionRef.current?.offsetHeight}px` : 'fit-content';

	return (
		<li
			key={item.id}
			className={classNames(
				`accordion-item-${item.id}`,
				styles.Wrapper,
			)}
		>
			<div
				className={classNames(
					'accordion-item',
					styles.Item,
				)}
			>
				<div className={classNames(styles.itemInnerContainer, "item-inner-container")} onClick={() => handleOpenItem(item.id)} tabIndex={tabIndex}>
					<div className={styles.itemContentContainer}>
						<TypographyItem className={classNames("accordion-item-title", titleOverrides)} tagName="h4">
							{title}
						</TypographyItem>
						<button
							className={classNames('clear no-style expand-accordion', styles.itemDropdownBtn)}
						>
							<span>
								{
									itemOpen
										? <Icon name={ICONS.KEYBOARD_ARROW_UP} color={''} size={20} />
										: <Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={''} size={20} />
								}
							</span>
						</button>
					</div>
					<AnimatePresence>
						<LazyMotion features={domAnimation} key={item.id}> {/* even though this is not in a loop, it still needs a key prop */}
							<motion.div
								initial={{ height: 0, overflow: "hidden" }}
								animate={
									itemOpen
										? {
											height: itemHeight,
											overflow: "visible",
										}
										: {}
								}
								exit={{ height: 0, overflow: "hidden" }}
							>
								<div className="accordion-item-description" ref={descriptionRef} onClick={e => e.stopPropagation()}>
									<RenderEditorDescription
										description={description}
										useNew
									/>
								</div>
							</motion.div>
						</LazyMotion>
					</AnimatePresence>
				</div>
			</div>
		</li >
	);
};

export default AccordionItem;
